// Engineer "My calendar" (Session AFF, Loop C). Replaces the old single-week list with a full
// Day / Week / Month calendar. The ONLY data source is useEngineerData().jobs -- the offline
// cache of the engineer's assigned open jobs plus recently submitted / completed ones -- so the
// screen works with no signal and updates reactively: when a job completes the context reloads,
// the job re-renders and its colour re-derives (green) with no page refresh. Jobs older than the
// cache window simply do not appear, which is acceptable and documented.
//
// A job's colour is always DERIVED from its live status at render time via calendarJobColour, so
// no colour state is stored. Colours use inline styles (dynamic hex cannot be a Tailwind class);
// the dark-PWA chrome uses static arbitrary-value classes so Tailwind's content scan keeps them.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, GraduationCap, X } from "lucide-react";
import { useEngineerData } from "./EngineerDataContext";
import { expandWorkDays, jobLinkOf, viewKeyOf } from "../../lib/engineerWorkDays";
import type { JobView } from "../../lib/offline/types";
import type { EngineerTrainingSessionDto } from "../../lib/trainingTypes";
import {
  calendarJobColour,
  weekDays,
  monthGrid,
  jobsOnDay,
  unscheduledJobs,
  hourSlots,
  bucketJobsByHour,
  addDays,
  addMonths,
  startOfLocalDay,
  isSameLocalDay,
  localDateKey,
} from "../../lib/engineerCalendar";

type CalendarView = "day" | "week" | "month";

const VIEW_LABELS: Record<CalendarView, string> = { day: "Day", week: "Week", month: "Month" };
const VIEWS: CalendarView[] = ["day", "week", "month"];
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// 20% alpha (0x33 = 51/255) appended to a hex colour for a soft pill / card background.
function tint(hex: string): string {
  return `${hex}33`;
}

// Training's fixed calendar colour (Session AGO): always the brand purple, NEVER derived through
// calendarJobColour -- training is not a job and has no job status to derive from.
const TRAINING_PURPLE = "#7d21c7";

// The training sessions falling on a local calendar day. sessionDate is date-only, stored at UTC
// midnight, so the day is read off the ISO string's OWN date part and compared to the cell's local
// key -- never a local conversion of midnight, which slips a day on a negative-UTC-offset machine.
function trainingOnDay(sessions: EngineerTrainingSessionDto[], day: Date): EngineerTrainingSessionDto[] {
  const key = localDateKey(day);
  return sessions.filter((s) => s.sessionDate.slice(0, 10) === key);
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function longDayLabel(d: Date): string {
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

function weekLabel(days: Date[]): string {
  const a = days[0];
  const b = days[6];
  const dayNum = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric" });
  const monthName = (d: Date) => d.toLocaleDateString("en-GB", { month: "long" });
  if (a.getMonth() === b.getMonth()) return `${dayNum(a)} to ${dayNum(b)} ${monthName(b)}`;
  return `${dayNum(a)} ${monthName(a)} to ${dayNum(b)} ${monthName(b)}`;
}

export function EngineerDiary() {
  // Session BDM: dayJobs lays a multi-day job out on every day the engineer is on; a one-day job is itself.
  const { jobs: ownJobs, online, loading, trainingSessions = [] } = useEngineerData();
  const jobs = useMemo(() => expandWorkDays(ownJobs), [ownJobs]);
  const [view, setView] = useState<CalendarView>("week");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [drawerDay, setDrawerDay] = useState<Date | null>(null);
  const now = new Date();

  // Escape closes the month-view day drawer (backdrop tap and the close control also close it).
  useEffect(() => {
    if (!drawerDay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerDay(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerDay]);

  const goPrev = () => {
    if (view === "day") setAnchor(addDays(anchor, -1));
    else if (view === "week") setAnchor(addDays(anchor, -7));
    else setAnchor(addMonths(anchor, -1));
  };
  const goNext = () => {
    if (view === "day") setAnchor(addDays(anchor, 1));
    else if (view === "week") setAnchor(addDays(anchor, 7));
    else setAnchor(addMonths(anchor, 1));
  };
  const goToday = () => setAnchor(new Date());

  const openDayFor = (day: Date) => {
    setAnchor(startOfLocalDay(day));
    setView("day");
    setDrawerDay(null);
  };

  const periodLabel =
    view === "day" ? longDayLabel(anchor) : view === "week" ? weekLabel(weekDays(anchor)) : anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div>
      <h1 className="mb-3 font-heading text-2xl tracking-wide text-ele-strong">My calendar</h1>

      {!online && <p className="mb-2 font-body text-xs text-ele-muted">Offline. Showing your saved jobs.</p>}

      {/* View switcher: Day | Week | Month. The active tab is the one purple-background control. */}
      <div role="tablist" aria-label="Calendar view" className="flex gap-1">
        {VIEWS.map((v) => {
          const active = view === v;
          return (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setView(v)}
              className={`min-h-[44px] flex-1 rounded-ele px-3 font-menu text-sm ${
                active ? "bg-[#7d21c7] text-white" : "text-ele-muted"
              }`}
            >
              {VIEW_LABELS[v]}
            </button>
          );
        })}
      </div>

      {/* Navigation: prev, period label, next, Today. */}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous"
          onClick={goPrev}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-ele border border-ele-card-border text-ele-muted"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 text-center font-heading text-base text-ele-strong">{periodLabel}</div>
        <button
          type="button"
          aria-label="Next"
          onClick={goNext}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-ele border border-ele-card-border text-ele-muted"
        >
          <ChevronRight size={20} />
        </button>
        <button
          type="button"
          onClick={goToday}
          className="min-h-[44px] rounded-ele border border-ele-card-border px-3 font-menu text-sm text-ele-strong"
        >
          Today
        </button>
      </div>

      <div className="mt-4">
        {loading && jobs.length === 0 ? (
          <p className="font-body text-sm text-ele-muted">Loading your calendar...</p>
        ) : view === "week" ? (
          <WeekView jobs={jobs} training={trainingSessions} anchor={anchor} now={now} />
        ) : view === "day" ? (
          <DayView jobs={jobs} training={trainingSessions} anchor={anchor} now={now} />
        ) : (
          <MonthView jobs={jobs} training={trainingSessions} anchor={anchor} now={now} onSelectDay={setDrawerDay} />
        )}
      </div>

      {drawerDay && (
        <DayDrawer jobs={jobs} training={trainingSessions} day={drawerDay} now={now} onClose={() => setDrawerDay(null)} onOpenDay={openDayFor} />
      )}
    </div>
  );
}

// --- Week view ------------------------------------------------------------
//
// A week strip of seven day buttons (short weekday, day number, count badge) and, under it, the
// selected day's items as a full-width stacked list. Nothing is truncated to a few letters or
// hidden behind "+N more": a long site name is cut only INSIDE its own tap target (DayCard's
// truncated line in a 44px Link), which opens the full job.

function itemCount(jobs: JobView[], training: EngineerTrainingSessionDto[], day: Date): number {
  return jobsOnDay(jobs, day).length + trainingOnDay(training, day).length;
}

// Today when it is in the shown week, else the first day of the week with anything, else Monday.
function defaultSelectedKey(days: Date[], jobs: JobView[], training: EngineerTrainingSessionDto[], now: Date): string {
  const today = days.find((d) => isSameLocalDay(d, now));
  if (today) return localDateKey(today);
  const busy = days.find((d) => itemCount(jobs, training, d) > 0);
  return localDateKey(busy ?? days[0]);
}

function WeekView({
  jobs,
  training,
  anchor,
  now,
}: {
  jobs: JobView[];
  training: EngineerTrainingSessionDto[];
  anchor: Date;
  now: Date;
}) {
  const days = weekDays(anchor);
  const weekKey = localDateKey(days[0]);
  // A tap is remembered with the week it was made in, so changing week re-applies the default.
  const [picked, setPicked] = useState<{ week: string; key: string } | null>(null);
  const selectedKey = picked && picked.week === weekKey ? picked.key : defaultSelectedKey(days, jobs, training, now);
  const selectedDay = days.find((d) => localDateKey(d) === selectedKey) ?? days[0];
  const hasAny = days.some((d) => itemCount(jobs, training, d) > 0);
  const selJobs = jobsOnDay(jobs, selectedDay);
  const selTraining = trainingOnDay(training, selectedDay);

  return (
    <div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const key = localDateKey(day);
          const isToday = isSameLocalDay(day, now);
          const selected = key === selectedKey;
          const count = itemCount(jobs, training, day);
          return (
            <button
              key={key}
              type="button"
              data-testid="cal-week-dayhead"
              data-selected={selected ? "true" : undefined}
              aria-pressed={selected}
              aria-label={`${longDayLabel(day)}, ${count === 0 ? "nothing booked" : count === 1 ? "1 item" : `${count} items`}`}
              onClick={() => setPicked({ week: weekKey, key })}
              className={`flex min-h-[44px] min-w-0 flex-col items-center justify-start rounded-ele px-0.5 py-1 leading-tight ${
                selected ? "bg-ele-purple text-white" : isToday ? "bg-ele-card text-ele-strong" : "text-ele-muted"
              }`}
            >
              <span className={`font-menu text-xs uppercase ${isToday || selected ? "font-bold" : ""}`}>
                {day.toLocaleDateString("en-GB", { weekday: "short" })}
              </span>
              <span className={`font-heading text-sm ${isToday || selected ? "font-bold" : ""}`}>{day.getDate()}</span>
              {/* The count sits on its own line (an empty slot keeps every day number level). */}
              <span className="mt-0.5 flex h-4 items-center justify-center">
                {count > 0 && (
                  <span
                    data-testid="cal-week-count"
                    aria-hidden="true"
                    className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-menu text-xs font-bold leading-none ${
                      selected ? "bg-white text-ele-purple" : "bg-ele-purple text-white"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </span>
              {selected && <span data-testid="cal-week-selected" className="sr-only">Selected</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        <h2 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-muted">{longDayLabel(selectedDay)}</h2>
        {selJobs.length === 0 && selTraining.length === 0 ? (
          <p className="font-body text-sm text-ele-muted">{hasAny ? "No jobs on this day." : "No jobs this week."}</p>
        ) : (
          <div data-testid="cal-week-list" className="space-y-2">
            {selTraining.map((s) => (
              <DayTrainingBar key={s.id} session={s} />
            ))}
            {selJobs.map((job) => (
              <DayCard key={viewKeyOf(job)} job={job} now={now} showTime />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// The day view's all-day training banner (Session AGO): a full-width solid purple bar above the
// timed slots, opening the training session.
function DayTrainingBar({ session }: { session: EngineerTrainingSessionDto }) {
  return (
    <Link
      to={`/engineer/training/${session.id}`}
      data-testid="cal-day-training"
      style={{ backgroundColor: TRAINING_PURPLE }}
      className="flex min-h-[44px] w-full items-center gap-2 rounded-ele px-3 py-2"
    >
      <GraduationCap size={16} color="#ffffff" className="flex-none" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-heading text-sm font-bold text-white">{session.typeLabel}</span>
        {/* Not text-white/80: `white` maps to var(--ele-white) and Tailwind v3 emits NOTHING for an
            alpha modifier on a var() colour (the AFK trap), leaving the line to inherit page ink. */}
        <span className="block truncate font-menu text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>
          {session.siteName}
        </span>
      </span>
    </Link>
  );
}

// --- Day view -------------------------------------------------------------

function DayView({
  jobs,
  training,
  anchor,
  now,
}: {
  jobs: JobView[];
  training: EngineerTrainingSessionDto[];
  anchor: Date;
  now: Date;
}) {
  const dayJobs = jobsOnDay(jobs, anchor);
  const dayTraining = trainingOnDay(training, anchor);
  const buckets = bucketJobsByHour(dayJobs);
  const unsched = unscheduledJobs(jobs);
  const slots = hourSlots();

  return (
    <div>
      {/* All-day training first, above everything timed (Session AGO). */}
      {dayTraining.length > 0 && (
        <div className="mb-4 space-y-2">
          {dayTraining.map((s) => (
            <DayTrainingBar key={s.id} session={s} />
          ))}
        </div>
      )}

      {unsched.length > 0 && (
        <section data-testid="cal-unscheduled" className="mb-4">
          <h2 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-muted">Unscheduled</h2>
          <div className="space-y-2">
            {unsched.map((job) => (
              <DayCard key={viewKeyOf(job)} job={job} now={now} showTime={false} />
            ))}
          </div>
        </section>
      )}

      {dayJobs.length === 0 && unsched.length === 0 && dayTraining.length === 0 && (
        <p className="mb-3 font-body text-sm text-ele-muted">No jobs booked for this day.</p>
      )}

      <div>
        {slots.map((slot) => {
          const slotJobs = buckets.get(slot.hour) ?? [];
          return (
            <div key={slot.hour} data-testid="cal-hour" className="flex gap-2 border-t border-ele-card-border py-1">
              <div className="w-14 shrink-0 pt-2 font-menu text-xs text-ele-muted">{slot.label}</div>
              <div className="min-w-0 flex-1 space-y-1 py-1">
                {slotJobs.map((job) => (
                  <DayCard key={viewKeyOf(job)} job={job} now={now} showTime />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A full-width day-view card. The 6px solid dot at the left is a functional time indicator
// (the one permitted left-side colour element), never a border stripe.
function DayCard({ job, now, showTime }: { job: JobView; now: Date; showTime: boolean }) {
  const colour = calendarJobColour(job, now);
  return (
    <Link
      to={jobLinkOf(job)}
      data-testid="cal-day-card"
      data-colour={colour}
      style={{ backgroundColor: tint(colour) }}
      className="flex min-h-[44px] items-center gap-2 rounded-ele px-3 py-2"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: colour }} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-heading text-sm font-bold text-ele-strong">{job.siteName || "Site to be confirmed"}</span>
        {job.eleNumber && <span className="block font-menu text-xs text-[var(--ele-loler-ink)]">{job.eleNumber}</span>}
      </span>
      {showTime && job.scheduledFor && <span className="shrink-0 font-menu text-xs text-ele-strong">{timeLabel(job.scheduledFor)}</span>}
    </Link>
  );
}

// --- Month view -----------------------------------------------------------

function MonthView({
  jobs,
  training,
  anchor,
  now,
  onSelectDay,
}: {
  jobs: JobView[];
  training: EngineerTrainingSessionDto[];
  anchor: Date;
  now: Date;
  onSelectDay: (day: Date) => void;
}) {
  const grid = monthGrid(anchor);
  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS.map((l) => (
          <div key={l} className="text-center font-menu text-xs uppercase text-ele-muted">
            {l}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {grid.map((week) => (
          <div key={localDateKey(week[0].date)} className="grid grid-cols-7 gap-0.5">
            {week.map((cell) => {
              const cellJobs = jobsOnDay(jobs, cell.date);
              const cellTraining = trainingOnDay(training, cell.date);
              const isToday = isSameLocalDay(cell.date, now);
              return (
                <button
                  key={localDateKey(cell.date)}
                  type="button"
                  data-testid="cal-month-cell"
                  data-iso={localDateKey(cell.date)}
                  onClick={() => onSelectDay(cell.date)}
                  className={`min-h-[44px] rounded p-1 text-left ${isToday ? "bg-ele-card" : ""} ${cell.inMonth ? "" : "opacity-40"}`}
                >
                  <div className={`font-heading text-xs ${isToday ? "font-bold text-ele-strong" : "text-ele-muted"}`}>{cell.date.getDate()}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-0.5">
                    {/* Training dots lead (all-day), always the fixed purple; their own testid so
                        the pinned job-dot queries stay honest. */}
                    {cellTraining.map((s) => (
                      <span
                        key={s.id}
                        data-testid="cal-month-training-dot"
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: TRAINING_PURPLE }}
                      />
                    ))}
                    {cellJobs.slice(0, 3).map((job) => (
                      <span
                        key={viewKeyOf(job)}
                        data-testid="cal-month-dot"
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: calendarJobColour(job, now) }}
                      />
                    ))}
                    {cellJobs.length > 3 && <span className="font-menu text-xs text-ele-muted">+{cellJobs.length - 3}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Month-view day drawer ------------------------------------------------

function DayDrawer({
  jobs,
  training,
  day,
  now,
  onClose,
  onOpenDay,
}: {
  jobs: JobView[];
  training: EngineerTrainingSessionDto[];
  day: Date;
  now: Date;
  onClose: () => void;
  onOpenDay: (day: Date) => void;
}) {
  const dayJobs = jobsOnDay(jobs, day);
  const dayTraining = trainingOnDay(training, day);
  const touchStart = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const start = touchStart.current;
    const current = e.touches[0]?.clientY;
    if (start != null && current != null && current - start > 60) {
      touchStart.current = null;
      onClose();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} data-testid="cal-drawer-backdrop" aria-hidden="true" />
      <div
        data-testid="cal-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Schedule for ${longDayLabel(day)}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        className="fixed inset-x-0 bottom-0 z-50 max-h-[75vh] overflow-y-auto rounded-t-2xl border-t border-ele-card-border bg-ele-card p-4"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ele-card-border" aria-hidden="true" />
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-heading text-base text-ele-strong">{longDayLabel(day)}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-ele text-ele-muted"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-2 space-y-2">
          {/* Training rows lead: a day showing only a purple training dot must never open an
              empty drawer (Session AGO). */}
          {dayTraining.map((s) => (
            <Link
              key={s.id}
              to={`/engineer/training/${s.id}`}
              data-testid="cal-drawer-training"
              className="flex min-h-[44px] items-center gap-2 rounded-ele border border-ele-card-border px-3 py-2"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TRAINING_PURPLE }} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-heading text-sm font-bold text-ele-strong">{s.typeLabel}</span>
                <span className="block truncate font-menu text-xs text-ele-muted">{s.siteName}</span>
              </span>
            </Link>
          ))}
          {dayJobs.length === 0 && dayTraining.length === 0 ? (
            <p className="font-body text-sm text-ele-muted">No jobs on this day.</p>
          ) : (
            dayJobs.map((job) => {
              const colour = calendarJobColour(job, now);
              return (
                <Link
                  key={viewKeyOf(job)}
                  to={jobLinkOf(job)}
                  data-testid="cal-drawer-job"
                  data-colour={colour}
                  className="flex min-h-[44px] items-center gap-2 rounded-ele border border-ele-card-border px-3 py-2"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: colour }} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-heading text-sm font-bold text-ele-strong">{job.siteName || "Site to be confirmed"}</span>
                    {job.eleNumber && <span className="block font-menu text-xs text-[var(--ele-loler-ink)]">{job.eleNumber}</span>}
                  </span>
                  {job.scheduledFor && <span className="shrink-0 font-menu text-xs text-ele-strong">{timeLabel(job.scheduledFor)}</span>}
                </Link>
              );
            })
          )}
        </div>

        <button
          type="button"
          onClick={() => onOpenDay(day)}
          className="mt-3 min-h-[44px] w-full rounded-ele border border-ele-card-border font-menu text-sm text-ele-strong"
        >
          Open day view
        </button>
      </div>
    </>
  );
}
